import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from "../../api/Base";

// Third-party packages.
import * as dayjs from 'dayjs';
import { CSVLink } from 'react-csv';
import { Column, usePagination, useSortBy, useTable } from 'react-table';

// Types
import Recommendation from '../../types/Recommendation';

// Helpers
import { archiverUrl, fetchResults, fetchWithAuthorization, getDefaultPageSize } from "../../helpers/utils";
import { showError } from '../../utils/showAlert';

// Components.
import SortClass from "../../component/SortClass";
import NoData from "../../component/NoData";
import TablePagination from "../../component/TablePagination";
import ConditionalRender from "../../helpers/ConditionalRender";
import SearchInput from '../../component/SearchInput';

function List() {
    const [tableFilter, setTableFilter] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<Recommendation[]>([]);
    const [recommendationType, setRecommendationType] = useState<string>('open');

    const loadData = async () => {
        setLoading(true);
        const params = {
            open: recommendationType === 'open',
            closed: recommendationType === 'closed',
            hidden: recommendationType === 'hidden',
        };
        const urlParams = new URLSearchParams(
                Object.entries(params).map(([key, value]) => [key, value.toString()])
        ).toString();

        try {
            const result: any[][] = await fetchResults([fetchWithAuthorization(archiverUrl() + `/recommendation?${urlParams}`)]);
            setLoading(false);
            setData(result[0]);
        } catch (error: any) {
            console.error(error.message);
            const errorMessage = error.message || error.toString();
            setError(errorMessage);
            showError('Failed to load recommendations', errorMessage);
            setData([]);
            setLoading(false)
        }
    };

    useEffect(() => {
        void loadData();
    }, [recommendationType]);


    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    const tableData = useMemo(() => data.filter((row) =>
                    row.category?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                    row.action?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                    row.label?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                    row.description?.toLowerCase().includes(tableFilter.toLowerCase())),
            [data, tableFilter]);

    const closeHide = async (id: number, isReset: boolean, isClose: boolean) => {
        setLoading(true);

        const url = `/recommendation`;

        // Update the relevant item in the array
        const updatedData = data.map(item =>
                item.id === id
                        ? {
                            ...item,
                            closed: isReset ? false : isClose || item.closed,
                            hidden: isReset ? false : !isClose || item.hidden,
                        }
                        : item
        );
        try {
            const result = await api.put(url, updatedData);
            if (result.status === 200) {
                await loadData();
            }
        } catch (error: any) {
            console.error("Failed to update recommendation:", error);
            const errorMessage = error.response?.data?.message || error.message || error.toString();
            showError('Failed to update recommendation', errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const columns: Column[] = useMemo(() => [
        {
            accessor: 'id',
            disableSortBy: true
        },
        {
            accessor: 'link',
            disableSortBy: true
        },
        {
            Header: 'Category',
            accessor: 'category',
        },
        {
            Header: 'Label',
            accessor: 'label',
        },
        {
            Header: 'Description',
            accessor: 'description',
        },
        {
            Header: 'Action',
            accessor: 'action',
            className: 'text-start',
            disableSortBy: true,
            Cell: ({ cell }) => {
                const action = cell.row.values.action;
                const link = cell.row.values.link;

                const isExternal = action.includes("docs.dbmarlin");

                return (
                        <Link
                                className="text-nowrap"
                                target="_blank"
                                rel={isExternal ? "noopener noreferrer" : undefined}
                                to={link}
                        >
                            {action}
                        </Link>
                );
            }
        },
        {
            Header: recommendationType === 'hidden'
                    ? 'Unhide'
                    : recommendationType === 'closed'
                            ? 'Unresolve'
                            : 'Resolve/Hide',
            accessor: 'resolve',
            className: 'actions',
            disableSortBy: true,
            headerClassName: 'actions',
            Cell: ({ cell }: any) => (
                    <React.Fragment>
                        <ConditionalRender if={recommendationType !== 'closed' && recommendationType !== 'hidden'}>
                            <button disabled={loading} type="button" className="btn btn-sm btn-success"
                                    onClick={() => closeHide(cell.row.values.id, false, true)}>
                                <i className="fa-regular fa-circle-exclamation-check"></i><span>Mark as resolved</span>
                            </button>
                            <button disabled={loading} type="button" className="btn btn-sm btn-primary"
                                    onClick={() => closeHide(cell.row.values.id, false, false)}>
                                <i className="fa-regular fa-eye-slash"></i><span>Hide</span>
                            </button>
                        </ConditionalRender>

                        <ConditionalRender if={recommendationType === 'closed'}>
                            <button disabled={loading} type="button" className="btn btn-sm btn-success"
                                    onClick={() => closeHide(cell.row.values.id, true, true)}>
                                <i className="fa-regular fa-circle-exclamation-check"></i><span>Mark as unresolved</span>
                            </button>
                        </ConditionalRender>

                        <ConditionalRender if={recommendationType === 'hidden'}>
                            <button disabled={loading} type="button" className="btn btn-sm btn-primary"
                                    onClick={() => closeHide(cell.row.values.id, true, true)}>
                                <i className="fa-regular fa-eye"></i><span>Unhide</span>
                            </button>
                        </ConditionalRender>
                    </React.Fragment>
            )
        }
    ], [tableData]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable({
                columns,
                data: tableData,
                initialState: {
                    hiddenColumns: ['id', 'link'],
                    pageIndex: 0,
                    pageSize: getDefaultPageSize()
                }
            },
            useSortBy, usePagination);

    const getLinkClass = (type: string) =>
            `${recommendationType === type ? 'active' : ''} pointer`;

    return (
            <div className="col h-100">
                <div id="menu-top" className="row row-cols-auto menu-top" role="tablist">
                    <button type="button" onClick={() => setRecommendationType('open')} className={getLinkClass('open')}>Open</button>
                    <button type="button" onClick={() => setRecommendationType('closed')} className={getLinkClass('closed')}>Resolved</button>
                    <button type="button" onClick={() => setRecommendationType('hidden')} className={getLinkClass('hidden')}>Hidden</button>

                </div>
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-file-certificate fa-fw mr-10" aria-hidden="true"></i>
                        Recommendations
                        <span className="badge bg-info"
                              data-tip="Total snapshot count">{data.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                           data-bs-target="#collapseRecommendations" aria-expanded="false" aria-controls="collapseRecommendations"></i>
                    </div>
                    <div id="collapseRecommendations" className="card-body collapse show">
                        <div className="row row-cols-1 row-cols-md-2 table-search">
                            <div className="col col-md-10">
                                <CSVLink
                                        role="button"
                                        headers={[
                                            { label: 'ID', key: 'id' },
                                            { label: 'Category', key: 'category' },
                                            { label: 'Label', key: 'label' },
                                            { label: 'Description', key: 'description' },
                                            { label: 'Action', key: 'fullLink' }
                                        ]}
                                        data={tableData.map(row => ({
                                            ...row,
                                            fullLink: `${window.location.origin}${row.link}` // Combine origin with link
                                        }))}
                                        download={`DBmarlin - ${dayjs.default().format('YYYY-MM-DD HH:mm')} - Recommendations.csv`}
                                        className="btn btn-sm btn-primary"
                                >
                                    <i className="fal fa-file-export fa-fw"></i><span>Export</span>
                                </CSVLink>
                            </div>
                            <div className="col col-md-2">
                                <SearchInput
                                    value={tableFilter}
                                    onChange={(e) => setTableFilter(e.target.value)}
                                    onClear={clearTableFilter}
                                    autoFocus
                                />
                            </div>
                        </div>


                        <div className="table-responsive">
                            <table {...getTableProps()} className="table">
                                <thead>
                                {headerGroups.map(headerGroup => (
                                        <tr {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map(column => (
                                                    <th {...column.getHeaderProps(column.getSortByToggleProps())}
                                                        className={(column as any).className}>
                                                        {column.render('Header')}
                                                        <SortClass column={column}/>
                                                    </th>
                                            ))}
                                        </tr>
                                ))}
                                </thead>
                                <tbody {...getTableBodyProps()}>
                                {page.map(row => {
                                    prepareRow(row)
                                    return (
                                            <tr {...row.getRowProps()}>
                                                {row.cells.map((cell) => {
                                                    return (
                                                            <td {...cell.getCellProps()} className={(cell.column as any).className}>
                                                                {cell.render('Cell')}
                                                            </td>
                                                    )
                                                })}
                                            </tr>
                                    )
                                })}
                                </tbody>
                            </table>
                            <NoData
                                    error={error}
                                    loading={loading}
                                    length={data.length}
                            />
                            <TablePagination
                                    length={data.length}
                                    pageSize={pageSize}
                                    setPageSize={setPageSize}
                                    pageOptions={pageOptions}
                                    pageCount={pageCount}
                                    canPreviousPage={canPreviousPage}
                                    previousPage={previousPage}
                                    canNextPage={canNextPage}
                                    nextPage={nextPage}
                                    gotoPage={gotoPage}
                                    pageIndex={pageIndex}
                            />
                        </div>
                    </div>
                </div>
            </div>
    )
}

export default List;
